import React from 'react';
import type { ScoreVariable, FormField, FieldOption } from '../../types/forms';

interface ScoreRangeDisplay {
  label: string;
  color?: string;
  icon?: string;
  message?: string | null;
}

interface ResponseScoreViewerProps {
  scores: Record<string, { totalPoints: number; range: ScoreRangeDisplay | null }>;
  variables: ScoreVariable[];
  fields?: FormField[];
  responseData?: Record<string, any>;
}

function getFieldContributions(
  field: FormField,
  answer: any
): { variableName: string; points: number }[] {
  const contributions: { variableName: string; points: number }[] = [];
  const options = (field.options || []).map(opt =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt
  ) as FieldOption[];

  const selectedValues = Array.isArray(answer) ? answer : [String(answer)];

  selectedValues.forEach(val => {
    const matched = options.find(o => o.value === val);
    matched?.scores?.forEach(s => {
      contributions.push({
        variableName: s.variableId,
        points: s.points,
      });
    });
  });

  return contributions;
}

export const ResponseScoreViewer: React.FC<ResponseScoreViewerProps> = ({
  scores,
  variables,
  fields,
  responseData,
}) => {
  if (!scores || !variables?.length) return null;

  return (
    <div className="space-y-4">
      {/* Score badges */}
      <div className="flex flex-wrap gap-2">
        {variables.map(variable => {
          const score = scores[variable.id];
          if (!score) return null;

          const range = score.range;
          const bgColor = range?.color ? `${range.color}20` : 'rgba(255,255,255,0.1)';
          const borderColor = range?.color ? `${range.color}40` : 'rgba(255,255,255,0.1)';
          const textColor = range?.color || '#a3a3a3';

          return (
            <span
              key={variable.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border"
              style={{ backgroundColor: bgColor, borderColor, color: textColor }}
            >
              {range?.label || `${score.totalPoints} pts`}
              <span className="text-neutral-500 ml-0.5">
                ({score.totalPoints})
              </span>
            </span>
          );
        })}
      </div>

      {/* Breakdown per field */}
      {fields && responseData && (
        <div className="mt-4 space-y-2">
          <h4 className="text-sm font-medium text-neutral-400 mb-2">
            Detalhamento por campo
          </h4>
          {fields.map(field => {
            const answer = responseData[field.id];
            if (!answer || !field.options?.length) return null;

            const fieldContributions = getFieldContributions(field, answer);
            if (!fieldContributions.length) return null;

            return (
              <div
                key={field.id}
                className="bg-neutral-900/50 rounded-xl p-3 text-sm"
              >
                <span className="text-neutral-400">{field.label}: </span>
                <span className="text-white">
                  {Array.isArray(answer) ? answer.join(', ') : String(answer)}
                </span>
                <div className="flex gap-2 mt-1">
                  {fieldContributions.map((c, i) => (
                    <span
                      key={i}
                      className="text-xs text-neutral-500"
                    >
                      +{c.points}pts ({c.variableName})
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
